import React from 'react';
import { TONE_BY_ZONE, type OverviewTone } from '../overviewMeta';
import { useOverviewMetric } from '../useOverviewMetric';

export type BreakEvenData = {
    tone: OverviewTone;
    revenueTetri: number;
    /** Signed: profit when positive, loss when negative. */
    resultTetri: number;
    /** Day of the month costs were covered, or `null` if they were not. */
    breakEvenDay: number | null;
    daysInMonth: number;
    /** Days earlier (+) or later (−) than last month, or `null`. */
    deltaDays: number | null;
};

/**
 * Break-even: the day this month's cash covered this month's costs.
 *
 * `breakEvenDay` stays nullable on a **computed** metric — a month that never
 * broke even is a real answer, not a missing one, and it is the answer the
 * danger state exists to show.
 */
export const useBreakEven = () => {
    const { metric, error, isLoading, missing, onRetry } =
        useOverviewMetric('breakEven');

    const data = React.useMemo<BreakEvenData | null>(() => {
        if (!metric || metric.state !== 'COMPUTED') {
            return null;
        }
        const { tone, revenueTetri, resultTetri } = metric;
        if (tone === null || revenueTetri === null || resultTetri === null) {
            return null;
        }
        return {
            tone: TONE_BY_ZONE[tone],
            revenueTetri,
            resultTetri,
            breakEvenDay: metric.breakEvenDay,
            daysInMonth: metric.daysInMonth,
            deltaDays: metric.deltaDays,
        };
    }, [metric]);

    // Stub: the expense-category flow arrives with the Expenses tab.
    const onEmptyAction = React.useCallback(() => {}, []);

    return { data, error, isLoading, missing, onRetry, onEmptyAction };
};
